import React from "react";
import { motion } from "framer-motion";
import { Database, Lightbulb, Hexagon, Layers, Microscope, Code2, ArrowRight } from "lucide-react";

export default function Services() {
    const consultingServices = [
        {
            icon: <Database size={28} className="text-white" />,
            title: "Data Management for Chemical Compounds",
            description: "We help you build a robust data infrastructure. From cleaning and standardizing legacy chemical data to implementing modern storage architectures that are AI-ready.",
            items: ["Data Standardization", "Infrastructure", "Legacy Migration"],
            color: "blue"
        },
        {
            icon: <Lightbulb size={28} className="text-white" />,
            title: "AI Integration Advisory",
            description: "Strategic roadmaps for adopting AI in your R&D pipeline. We assess your current capabilities and identify high-impact areas where AI can accelerate discovery.",
            items: ["ROI Assessment", "Workflow Integration", "Team Training"],
            color: "indigo"
        }
    ];

    const solutionServices = [
        {
            icon: <Layers size={28} className="text-white" />,
            title: "Chemical Compound Registry",
            description: "A centralized, intelligent registry system to track, manage, and retrieve unique chemical entities across your organization. Eliminate duplicates and ensure data integrity.",
            items: ["Unique Identifiers", "Structure Search", "Metadata Management"],
            color: "purple"
        },
        {
            icon: <Microscope size={28} className="text-white" />,
            title: "Target-specific Compound Generation",
            description: "Leverage our proprietary generative models to design novel compounds optimized for your specific biological targets, with high predicted affinity and drug-likeness.",
            items: ["Generative AI", "Structure-Based Design", "Multi-parameter Opt."],
            color: "pink"
        },
        {
            icon: <Hexagon size={28} className="text-white" />,
            title: "Compound Library Generation",
            description: "Generate vast, diverse virtual libraries of synthetically accessible compounds around specific scaffolds or properties for virtual screening campaigns.",
            items: ["Diversity Analysis", "Virtual Screening", "Scaffold Hopping"],
            color: "cyan"
        },
        {
            icon: <Code2 size={28} className="text-white" />,
            title: "Custom AI Solutions",
            description: "Tailored AI models developed for your unique niche challenges, from predicting specific toxicity endpoints to analyzing phenotypic screening images.",
            items: ["Custom Models", "Image Analysis", "NLP for Chem"],
            color: "emerald"
        }
    ];

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0 }
    };

    const getColorClasses = (color) => {
        const colors = {
            blue: "bg-blue-500 from-blue-400 to-blue-600 shadow-blue-200",
            indigo: "bg-indigo-500 from-indigo-400 to-indigo-600 shadow-indigo-200",
            purple: "bg-purple-500 from-purple-400 to-purple-600 shadow-purple-200",
            pink: "bg-pink-500 from-pink-400 to-pink-600 shadow-pink-200",
            cyan: "bg-cyan-500 from-cyan-400 to-cyan-600 shadow-cyan-200",
            emerald: "bg-emerald-500 from-emerald-400 to-emerald-600 shadow-emerald-200"
        };
        return colors[color] || colors.blue;
    };

    return (
        <div className="bg-slate-50 min-h-screen">
            {/* Header */}
            <section className="relative py-24 px-6 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"></div>
                <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-blue-900/20 to-transparent"></div>
                <div className="absolute bottom-0 left-0 w-1/2 h-full bg-gradient-to-r from-purple-900/20 to-transparent"></div>

                <div className="max-w-4xl mx-auto text-center relative z-10">
                    <motion.span
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-blue-400 font-semibold tracking-wider uppercase text-sm"
                    >
                        Our Expertise
                    </motion.span>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-5xl md:text-6xl font-extrabold text-white mt-4 mb-6 tracking-tight"
                    >
                        Accelerating Discovery with<br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">AI & Data Intelligence</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-xl text-slate-300 max-w-2xl mx-auto font-light leading-relaxed"
                    >
                        We offer specialized consulting and advanced AI solutions to transform how you manage chemical data and discover new drugs.
                    </motion.p>
                </div>
            </section>

            {/* Consulting Section */}
            <section className="max-w-7xl mx-auto px-6 py-20">
                <div className="flex items-center gap-4 mb-12">
                    <div className="h-px bg-slate-300 flex-1"></div>
                    <h2 className="text-3xl font-bold text-slate-800 uppercase tracking-widest text-center px-4">AI Consulting & Advisory</h2>
                    <div className="h-px bg-slate-300 flex-1"></div>
                </div>

                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    className="grid grid-cols-1 md:grid-cols-2 gap-8"
                >
                    {consultingServices.map((service, index) => (
                        <motion.div
                            key={index}
                            variants={itemVariants}
                            className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 border border-slate-100 group"
                        >
                            <div className="flex items-start gap-6">
                                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${getColorClasses(service.color)} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300 shrink-0`}>
                                    {service.icon}
                                </div>
                                <div>
                                    <h3 className="text-2xl font-bold text-slate-900 mb-3 group-hover:text-blue-600 transition-colors">{service.title}</h3>
                                    <p className="text-slate-600 leading-relaxed mb-6">
                                        {service.description}
                                    </p>
                                    <ul className="space-y-2">
                                        {service.items.map((item, i) => (
                                            <li key={i} className="flex items-center gap-2 text-sm text-slate-500 font-medium">
                                                <div className={`w-1.5 h-1.5 rounded-full bg-${service.color}-500`}></div>
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </motion.div>

                <div className="mt-12 text-center">
                    <a href="/contact" className="inline-flex items-center gap-2 px-8 py-3 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 transition-colors shadow-lg shadow-slate-900/20">
                        Hire a Consultant
                    </a>
                </div>
            </section>

            {/* Solutions Section */}
            <section className="bg-white py-20 border-t border-slate-100">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="flex items-center gap-4 mb-12">
                        <div className="h-px bg-slate-200 flex-1"></div>
                        <h2 className="text-3xl font-bold text-slate-800 uppercase tracking-widest text-center px-4">AI Solutions</h2>
                        <div className="h-px bg-slate-200 flex-1"></div>
                    </div>

                    <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        className="grid grid-cols-1 md:grid-cols-2 gap-8"
                    >
                        {solutionServices.map((service, index) => (
                            <motion.div
                                key={index}
                                variants={itemVariants}
                                className="group relative overflow-hidden bg-slate-50 rounded-2xl p-8 hover:bg-white hover:shadow-xl transition-all duration-300 border border-slate-100"
                            >
                                <div className={`absolute top-0 right-0 w-32 h-32 bg-${service.color}-100 rounded-full blur-3xl opacity-0 group-hover:opacity-50 transition-opacity`}></div>

                                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${getColorClasses(service.color)} flex items-center justify-center shadow-md mb-6`}>
                                    {service.icon}
                                </div>

                                <h3 className="text-xl font-bold text-slate-900 mb-3">{service.title}</h3>
                                <p className="text-slate-600 mb-6 text-sm leading-relaxed">
                                    {service.description}
                                </p>

                                <div className="flex flex-wrap gap-2">
                                    {service.items.map((item, i) => (
                                        <span key={i} className="px-3 py-1 bg-white border border-slate-200 rounded-full text-xs font-semibold text-slate-600 shadow-sm">
                                            {item}
                                        </span>
                                    ))}
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>

                    <div className="mt-12 text-center">
                        <a href="/contact" className="inline-flex items-center gap-2 px-8 py-3 bg-white border border-slate-200 text-slate-700 rounded-xl font-bold hover:bg-slate-50 transition-colors shadow-sm">
                            Request a Demo
                        </a>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="max-w-5xl mx-auto px-6 py-20">
                <div className="relative overflow-hidden bg-slate-900 rounded-3xl p-12 text-center shadow-2xl">
                    <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 opacity-50"></div>
                    <div className="relative z-10">
                        <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Start Your Transformation Today</h2>
                        <p className="text-blue-100 mb-8 text-lg max-w-2xl mx-auto">
                            Whether you need strategic guidance or powerful AI tools, we are ready to help you accelerate your chemical R&D.
                        </p>
                        <a href="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-white text-slate-900 rounded-xl font-bold hover:bg-blue-50 transition-colors shadow-lg hover:shadow-white/20">
                            Book a Consultation <ArrowRight size={18} />
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
}

